import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Buildings, Scales, Receipt, LockKey, IdentificationCard, ArrowSquareOut, CheckCircle } from "@phosphor-icons/react";

const FIELD_GROUPS = [
  {
    title: "Firma",
    icon: <Buildings size={16} weight="bold" />,
    fields: [
      { k: "legal_name", label: "Firmenname (offiziell)*", ph: "Max Mustermann Tonnenreinigung" },
      { k: "legal_form", label: "Rechtsform", ph: "UG (haftungsbeschränkt) / GmbH / Einzelunternehmen" },
      { k: "public_display_name", label: "Öffentlicher Anzeigename (optional)", ph: "MMT Reinigung" },
    ],
  },
  {
    title: "Anschrift",
    icon: <IdentificationCard size={16} weight="bold" />,
    fields: [
      { k: "street", label: "Straße + Hausnummer*", ph: "Bahnhofstraße 12" },
      { k: "zip", label: "PLZ*", ph: "79576" },
      { k: "city", label: "Ort*", ph: "Weil am Rhein" },
      { k: "country", label: "Land", ph: "Deutschland" },
    ],
  },
  {
    title: "Kontakt",
    icon: <Buildings size={16} weight="bold" />,
    fields: [
      { k: "contact_email", label: "E-Mail (Impressum)*", ph: "info@mustermann.de" },
      { k: "contact_phone", label: "Telefon", ph: "+49 7621 123456" },
      { k: "website", label: "Website", ph: "https://mustermann.de" },
    ],
  },
  {
    title: "Register & Steuer",
    icon: <Receipt size={16} weight="bold" />,
    fields: [
      { k: "hrb", label: "Handelsregister-Nr.", ph: "HRB 12345" },
      { k: "court", label: "Registergericht", ph: "Amtsgericht Freiburg" },
      { k: "vat_id", label: "USt-IdNr.", ph: "DE123456789" },
      { k: "tax_id", label: "Steuernummer", ph: "12345/67890" },
    ],
  },
  {
    title: "Verantwortlich",
    icon: <Scales size={16} weight="bold" />,
    fields: [
      { k: "managing_director", label: "Geschäftsführer / Inhaber*", ph: "Max Mustermann" },
      { k: "responsible_person", label: "Verantwortlich i.S.d. § 55 RStV", ph: "Max Mustermann" },
      { k: "supervisory_authority", label: "Aufsichtsbehörde", ph: "z.B. IHK Hochrhein-Bodensee" },
      { k: "professional_liability_insurer", label: "Berufshaftpflicht-Versicherer", ph: "z.B. Allianz Versicherungs-AG" },
      { k: "professional_liability_area", label: "Geltungsbereich der Versicherung", ph: "Deutschland" },
    ],
  },
  {
    title: "Datenschutz",
    icon: <LockKey size={16} weight="bold" />,
    fields: [
      { k: "dpo_name", label: "Datenschutzbeauftragter Name", ph: "Max Mustermann" },
      { k: "dpo_email", label: "Datenschutzbeauftragter E-Mail", ph: "datenschutz@mustermann.de" },
    ],
  },
];

export default function MasterDataPanel({ tenant, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);
  const [agbForm, setAgbForm] = useState({ agb_intro: "", agb_notice_period_days: 30, agb_cancellation_email: "" });

  useEffect(() => {
    const m = tenant?.master_data || {};
    setForm(m);
    setAgbForm({
      agb_intro: m.agb_intro || "",
      agb_notice_period_days: m.agb_notice_period_days || 30,
      agb_cancellation_email: m.agb_cancellation_email || "",
    });
  }, [tenant]);

  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/master-data`, { ...form, ...agbForm });
      onSaved?.(r.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const completeness = () => {
    const required = ["legal_name", "street", "zip", "city", "contact_email", "managing_director"];
    const filled = required.filter((k) => (form[k] || "").trim()).length;
    return Math.round((filled / required.length) * 100);
  };
  const pct = completeness();

  return (
    <div className="space-y-6" data-testid="master-data-panel">
      {/* Overview */}
      <section className="bg-gradient-to-br from-emerald-50 to-white border border-black/10 p-5">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2">
              <IdentificationCard size={18} weight="bold" /> Stammdaten &amp; Rechtstexte
            </h3>
            <p className="text-xs text-zinc-500 mt-1 leading-relaxed max-w-xl">
              Diese Angaben erscheinen automatisch in <strong>Impressum</strong>, <strong>AGB</strong> und <strong>Datenschutzerklärung</strong> Ihres Portals. Verpflichtend nach § 5 TMG.
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">Vollständigkeit</div>
            <div className="flex items-center gap-2">
              <div className="w-40 h-2 bg-zinc-200 rounded-full overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${pct}%`, background: pct >= 100 ? "#10B981" : "#F59E0B" }} />
              </div>
              <div className="font-mono text-sm font-bold" data-testid="masterdata-completeness">{pct}%</div>
            </div>
          </div>
        </div>
        {pct >= 100 && (
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <a href={`/portal/${tenant?.slug}/impressum`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 border border-black/10 hover:bg-white" data-testid="preview-impressum-link">
              <ArrowSquareOut size={11} weight="bold" /> Impressum ansehen
            </a>
            <a href={`/portal/${tenant?.slug}/agb`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 border border-black/10 hover:bg-white" data-testid="preview-agb-link">
              <ArrowSquareOut size={11} weight="bold" /> AGB ansehen
            </a>
            <a href={`/portal/${tenant?.slug}/datenschutz`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 border border-black/10 hover:bg-white" data-testid="preview-datenschutz-link">
              <ArrowSquareOut size={11} weight="bold" /> Datenschutz ansehen
            </a>
          </div>
        )}
      </section>

      {/* Field groups */}
      {FIELD_GROUPS.map((g) => (
        <section key={g.title} className="bg-white border border-black/10 p-5">
          <h4 className="font-heading font-semibold tracking-tight flex items-center gap-2 mb-4">
            {g.icon} {g.title}
          </h4>
          <div className="grid md:grid-cols-2 gap-3">
            {g.fields.map((f) => (
              <div key={f.k}>
                <Label className="text-xs">{f.label}</Label>
                <Input
                  className="h-10 rounded-none"
                  placeholder={f.ph}
                  value={form[f.k] || ""}
                  onChange={(e) => setForm({ ...form, [f.k]: e.target.value })}
                  data-testid={`masterdata-${f.k}`}
                />
              </div>
            ))}
          </div>
        </section>
      ))}

      {/* AGB overrides */}
      <section className="bg-white border border-black/10 p-5">
        <h4 className="font-heading font-semibold tracking-tight flex items-center gap-2 mb-1">
          <Scales size={16} weight="bold" /> AGB-Anpassungen
        </h4>
        <p className="text-xs text-zinc-500 mb-4">Optional: Standard-Textbausteine der AGB überschreiben.</p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Individueller AGB-Einleitungstext (optional)</Label>
            <Textarea
              rows={3}
              className="rounded-none"
              placeholder="Diese AGB regeln das Vertragsverhältnis zwischen [Firma] und dem Kunden…"
              value={agbForm.agb_intro || ""}
              onChange={(e) => setAgbForm({ ...agbForm, agb_intro: e.target.value })}
              data-testid="masterdata-agb-intro"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Kündigungsfrist in Tagen</Label>
              <Input type="number" className="h-10 rounded-none" value={agbForm.agb_notice_period_days} onChange={(e) => setAgbForm({ ...agbForm, agb_notice_period_days: +e.target.value })} data-testid="masterdata-agb-notice" />
            </div>
            <div>
              <Label className="text-xs">Kündigungs-E-Mail</Label>
              <Input className="h-10 rounded-none" placeholder="kuendigung@..." value={agbForm.agb_cancellation_email} onChange={(e) => setAgbForm({ ...agbForm, agb_cancellation_email: e.target.value })} data-testid="masterdata-agb-cancel-email" />
            </div>
          </div>
        </div>
      </section>

      {/* Sticky save bar */}
      <div className="sticky bottom-4 bg-white/95 backdrop-blur-sm border border-black/10 p-3 flex items-center justify-between shadow-lg" data-testid="masterdata-save-bar">
        <div className="text-xs text-zinc-500">
          {err ? <span className="text-red-600">✗ {err}</span> : saved ? <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Gespeichert · Impressum, AGB und Datenschutz sind aktualisiert.</span> : "Änderungen erscheinen sofort im Portal."}
        </div>
        <Button onClick={save} disabled={saving} className="rounded-none bg-black text-white hover:bg-zinc-800 h-10" data-testid="masterdata-save-btn">
          {saving ? "Speichere…" : "Speichern"}
        </Button>
      </div>
    </div>
  );
}
